import React, { Component, createRef } from 'react';

import ConversationItem from '@components/ConversationItem/ConversationItem';
import { filterService } from '@services/filterService';
import type { SearchFilterParams } from '@services/filterService';
import type { ConversationDetail, Reply } from '../../types/conversation';
import type { ApiConfig, AppConfig, CorgeeFunctions } from '@components/ConversationItem/types';

import InputFilter from './InputFilter';
import './TopSearchResult.css';

// ==================== Types ====================

interface TopSearchResultRequest {
  api: ApiConfig;
  functions?: TopSearchResultFunctions;
  params?: {
    RealItemTypes?: any;
    HasItemCreationPermission?: boolean;
  };
  config?: AppConfig;
  storageData?: any;
  chatData?: any;
  userData?: any;
}

interface TopSearchResultFunctions {
  getSocketConnectionId?: () => string;
  onSubmitTime?: (conversation: ConversationDetail) => void;
  saveSearchResult?: (result: any) => void;
  onHidePopover?: () => void;
  onClearSearch?: () => void;
  onUpdateConversation?: (
    id: number,
    newAttrs: Partial<ConversationDetail>,
    conversation: ConversationDetail,
    renderType?: string
  ) => void;
  onAddReply?: (convId: number, reply: Reply, conversation: ConversationDetail) => void;
  onUpdateReply?: (
    conversation: ConversationDetail,
    reply: Reply,
    newAttrs: Partial<Reply>,
    newTotalFavorites?: number
  ) => void;
  onCreateItemFromCustomType?: (item: any, conversation: ConversationDetail) => void;
  onItemConvertTo?: (conversation: ConversationDetail) => void;
  onDuplicateItem?: (conversation: ConversationDetail) => void;
  addLayerToCard?: (
    e: React.MouseEvent | MouseEvent,
    conversation: ConversationDetail,
    layer: any,
    callback: (layerId: number) => void
  ) => void;
  attachReactFunctions?: (functions: Record<string, any>) => void;
  loadingBarStart?: () => void;
  loadingBarComplete?: () => void;
}

export interface TopSearchResultProps {
  request: TopSearchResultRequest;
  corgeeFunctions?: CorgeeFunctions;
  width?: number;
  height?: number;
  heightContainer?: number;
  word?: string;
}

interface TopSearchResultState {
  show: boolean;
  width: number;
  height: number;
  heightContainer: number;
  conversations: ConversationDetail[];
  isLoading: boolean;
  isLoadingFilter: boolean;
  left: number;
  word: string;
  filters: any[];
  isViewCard: boolean;
  hasArchivedFilter: boolean;
  requestParams: SearchFilterParams;
  loadMoreInProgress: boolean;
  pageSize: number;
  hasMoreItems: boolean;
  currentPage: number;
  fullScreen: boolean;
  isErrorFetch: boolean;
  configValueId?: number;
  isFromProject?: boolean;
  projectId?: number;
  targetRect: { left: number; top: number; width: number } | null;
}

// ==================== Component ====================

class TopSearchResult extends Component<TopSearchResultProps, TopSearchResultState>
{
  private popoverRef = createRef<HTMLDivElement>();
  private sentinelRef = createRef<HTMLLIElement>();
  private intersectionObserver: IntersectionObserver | null = null;

  constructor(props: TopSearchResultProps)
  {
    super(props);

    this.state = {
      show: false,
      width: props.width ?? 750,
      height: props.height ?? window.innerHeight - 120,
      heightContainer: props.heightContainer ?? 400,
      conversations: [],
      isLoading: false,
      isLoadingFilter: false,
      left: 200,
      word: props.word ?? '',
      filters: [],
      isViewCard: false,
      hasArchivedFilter: false,
      requestParams: {} as SearchFilterParams,
      loadMoreInProgress: false,
      pageSize: 30,
      hasMoreItems: true,
      currentPage: 1,
      fullScreen: false,
      isErrorFetch: false,
      targetRect: null,
    };
  }

  componentDidMount()
  {
    // Propagate react functions to parent
    if (this.props.request.functions?.attachReactFunctions)
    {
      const paramFunctions = {
        onShow: this.show,
        onHide: this.hide,
        onFullScreen: this.fullScreen,
        onUpdateConversation: this.onUpdateConversationExternal,
        onAddReply: this.onAddReply,
        onCreateNewItem: this.onCreateNewItem,
        onConvertTo: this.onConvertTo,
        onUpdateReply: this.onUpdateExternalReply,
        onDeleteConversation: this.onDeleteConversation,
        onRestoreConversation: this.onRestoreConversation,
        onDeleteReply: this.onDeleteReply,
        onDuplicateItem: this.onDuplicateItem,
      };
      this.props.request.functions.attachReactFunctions(paramFunctions);
    }

    window.addEventListener('resize', this.updateDimensions);
  }

  componentWillUnmount()
  {
    window.removeEventListener('resize', this.updateDimensions);
    this.cleanupIntersectionObserver();
  }

  componentDidUpdate(_prevProps: TopSearchResultProps, _prevState: TopSearchResultState)
  {
    // Setup IntersectionObserver when conversations are loaded and there are more items
    if (
      this.state.conversations.length > 0 &&
      this.state.hasMoreItems &&
      !this.state.loadMoreInProgress &&
      !this.state.isLoading &&
      !this.state.isLoadingFilter
    )
    {
      this.setupIntersectionObserver();
    }
  }

  // ==================== IntersectionObserver (replaces react-waypoint) ====================

  private setupIntersectionObserver = () =>
  {
    this.cleanupIntersectionObserver();

    if (!this.sentinelRef.current) return;

    this.intersectionObserver = new IntersectionObserver(
      entries =>
      {
        if (
          entries[0]?.isIntersecting &&
          this.state.hasMoreItems &&
          !this.state.loadMoreInProgress
        )
        {
          this.getMoreConversations();
        }
      },
      { threshold: 0.1 },
    );

    this.intersectionObserver.observe(this.sentinelRef.current);
  };

  private cleanupIntersectionObserver = () =>
  {
    if (this.intersectionObserver)
    {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
  };

  // ==================== Socket ====================

  private getSocketConnectionId = (): string | undefined =>
  {
    return this.props.request.functions?.getSocketConnectionId?.();
  };

  // ==================== Conversation Callbacks ====================

  private onSubmitTime = (conversation: ConversationDetail) =>
  {
    const conversationsCopy = [...this.state.conversations];
    const indexConv = conversationsCopy.findIndex(
      item => conversation.ConversationId === item.ConversationId,
    );
    if (indexConv >= 0)
    {
      conversationsCopy[indexConv] = {
        ...conversationsCopy[indexConv]!,
        TotalReplies: conversationsCopy[indexConv]!.TotalReplies + 1,
      } as any;
      this.setState({ conversations: conversationsCopy });
    }

    this.props.request.functions?.onSubmitTime?.(conversation);
  };

  private getMoreConversations = () =>
  {
    this.setState({ loadMoreInProgress: true, isErrorFetch: false });

    this.props.corgeeFunctions?.loadingBarStart?.();

    const requestParamsCopy = { ...this.state.requestParams };
    const currentPageNumber = this.state.currentPage + 1;

    requestParamsCopy.ConfigValueId2 = this.state.configValueId;
    requestParamsCopy.PageNumber = currentPageNumber;

    filterService
      .getConversationsBySearchFilter(requestParamsCopy)
      .then(result =>
      {
        const hasMoreItems = result.length >= this.state.pageSize;
        const newArray = [...this.state.conversations, ...result];

        this.setState({
          conversations: newArray,
          loadMoreInProgress: false,
          hasMoreItems,
          currentPage: currentPageNumber,
        });

        this.props.corgeeFunctions?.loadingBarComplete?.();
      })
      .catch(() =>
      {
        this.setState({ loadMoreInProgress: false });
      });
  };

  private saveSearchResult = (e: React.MouseEvent<HTMLButtonElement>) =>
  {
    e.preventDefault();

    const requestParamsCopy = { ...this.state.requestParams };
    requestParamsCopy.ConfigValueId2 = this.state.configValueId;
    requestParamsCopy.PageNumber = 1;
    requestParamsCopy.PageSize = 500;

    if (this.state.isFromProject)
    {
      requestParamsCopy.Project = this.state.projectId;
    }

    if (requestParamsCopy.Filters === 'null')
    {
      requestParamsCopy.Filters = '';
    }

    this.props.corgeeFunctions?.loadingBarStart?.();

    filterService
      .addResultToDashboard(requestParamsCopy)
      .then(result =>
      {
        this.props.request.functions?.saveSearchResult?.(result);
        this.setState({ show: false });
        this.props.corgeeFunctions?.loadingBarComplete?.();
      })
      .catch(error =>
      {
        console.error(error);
      });
  };

  private showCardView = (e: React.MouseEvent<HTMLButtonElement>) =>
  {
    e.preventDefault();
    this.setState({ isViewCard: true });
  };

  private showTileView = (e: React.MouseEvent<HTMLButtonElement>) =>
  {
    e.preventDefault();
    this.setState({ isViewCard: false });
  };

  private getConversationByFilters = (filters: any[]) =>
  {
    const requestParams: SearchFilterParams = {
      SearchText: this.state.word,
      Filters: filters ? JSON.stringify(filters) : '',
      PageNumber: 1,
      PageSize: this.state.pageSize,
    };

    for (const element of filters)
    {
      if (element !== undefined && element !== null)
      {
        if (!element.IsCustomField)
        {
          if (this.isNumeric(element.Value))
          {
            (requestParams as any)[element.Name] = parseInt(element.Value);
          }
          else
          {
            (requestParams as any)[element.Name] = element.Value;
          }
        }
        else
        {
          if (requestParams.CustomFields === undefined)
          {
            requestParams.CustomFields = [];
          }
          requestParams.CustomFields.push({
            Name: element.Name,
            FieldTypeId: element.FieldTypeId,
            Value: element.Value,
            ComparisonOperator: element.ComparisonOperator,
          });
        }
      }
    }

    let hasArchivedFilter = false;
    const filtersFields = requestParams.Filters;
    if (filtersFields)
    {
      try
      {
        const parsed = JSON.parse(filtersFields);
        if (Array.isArray(parsed))
        {
          parsed.forEach((b: any) =>
          {
            if (b !== null && b.Name !== undefined && b.Name.toUpperCase() === 'ARCHIVED')
            {
              hasArchivedFilter = true;
            }
          });
        }
      }
      catch
      {
        // Invalid JSON, ignore
      }
    }

    this.setState(
      { isLoadingFilter: true, hasArchivedFilter, requestParams, isErrorFetch: false },
      () =>
      {
        this.props.corgeeFunctions?.loadingBarStart?.();

        filterService
          .getConversationsBySearchFilter(requestParams)
          .then(result =>
          {
            const hasMoreItems = result.length >= this.state.pageSize;
            this.setState({ conversations: result, isLoadingFilter: false, hasMoreItems });
            this.props.corgeeFunctions?.loadingBarComplete?.();
          })
          .catch(() =>
          {
            this.setState({ isLoadingFilter: false, isErrorFetch: true });
          });
      },
    );
  };

  private isNumeric = (n: any): boolean =>
  {
    return !isNaN(parseFloat(n)) && isFinite(n);
  };

  // ==================== Show / Hide / FullScreen ====================

  show = (
    e: any,
    params: { ConfigValueId?: number; IsFromProject?: boolean; ProjectId?: number },
  ) =>
  {
    const inputControl = e.target ? e.target : e;
    const word = inputControl.value ? inputControl.value.trim() : inputControl.value;

    const rect = inputControl.getBoundingClientRect
      ? inputControl.getBoundingClientRect()
      : { left: 200, top: 80 };

    this.setState(
      {
        show: true,
        word,
        isLoading: true,
        configValueId: params.ConfigValueId,
        isFromProject: params.IsFromProject,
        projectId: params.ProjectId,
        isErrorFetch: false,
        targetRect: {
          left: rect.left,
          top: rect.bottom || rect.top + 40,
          width: rect.width || 480,
        },
      },
      () =>
      {
        const requestParams: SearchFilterParams = {
          SearchText: word,
          Filters: '',
          PageNumber: 1,
          PageSize: this.state.pageSize,
        };

        filterService
          .getConversationsBySearchFilter(requestParams)
          .then(result =>
          {
            const hasMoreItems = result.length >= this.state.pageSize;
            this.setState({
              conversations: result,
              isLoading: false,
              requestParams,
              hasMoreItems,
            });
          })
          .catch(() =>
          {
            this.setState({ isLoading: false, isLoadingFilter: false, isErrorFetch: true });
          });
      },
    );
  };

  hide = () =>
  {
    this.setState({ show: false, hasArchivedFilter: false, fullScreen: false });
    this.cleanupIntersectionObserver();
    this.props.request.functions?.onHidePopover?.();
  };

  hideAndClear = () =>
  {
    this.setState({ show: false, hasArchivedFilter: false, fullScreen: false, word: '' });
    this.cleanupIntersectionObserver();
    this.props.request.functions?.onHidePopover?.();
    this.props.request.functions?.onClearSearch?.();
  };

  fullScreen = () =>
  {
    this.setState(prevState => ({ fullScreen: !prevState.fullScreen }));
  };

  // ==================== Conversation State Management ====================

  private onUpdateConversation = (
    id: number,
    newAttrs: Partial<ConversationDetail>,
    sendUpdate = true,
    renderType?: string,
  ) =>
  {
    const conversationsCopy = [...this.state.conversations];
    const indexConv = conversationsCopy.findIndex(item => item.ConversationId === id);

    if (indexConv >= 0 && conversationsCopy[indexConv])
    {
      conversationsCopy[indexConv] = { ...conversationsCopy[indexConv], ...newAttrs };
      this.setState({ conversations: conversationsCopy });

      if (sendUpdate)
      {
        this.props.request.functions?.onUpdateConversation?.(
          id,
          newAttrs,
          conversationsCopy[indexConv],
          renderType,
        );
      }
    }
  };

  private onUpdateConversationExternal = (
    id: number,
    newAttrs: any,
    replace?: boolean,
    highlight = false,
  ) =>
  {
    const conversationsCopy = [...this.state.conversations];
    const indexConv = conversationsCopy.findIndex(item => item.ConversationId === id);

    if (indexConv >= 0 && conversationsCopy[indexConv])
    {
      if (highlight)
      {
        newAttrs.Highlight = true;
      }

      if (replace)
      {
        conversationsCopy[indexConv] = newAttrs;
      }
      else
      {
        conversationsCopy[indexConv] = { ...conversationsCopy[indexConv], ...newAttrs };
      }

      this.setState({ conversations: conversationsCopy });
    }
  };

  private onDeleteConversation = (convId: number) =>
  {
    const conversationsCopy = [...this.state.conversations];
    const indexConvToDelete = conversationsCopy.findIndex(item => item.ConversationId === convId);

    if (indexConvToDelete >= 0)
    {
      conversationsCopy[indexConvToDelete] = {
        ...conversationsCopy[indexConvToDelete],
        IsDeleted2: true,
      } as any;
    }

    // Search subitems
    let replySubItem: Reply | undefined;
    conversationsCopy.forEach(element =>
    {
      if (replySubItem === undefined && element.Replies)
      {
        replySubItem = element.Replies.find(
          (replyElement: any) =>
            replyElement !== undefined && replyElement.OriginalConversationId === convId,
        );
      }
    });

    if (replySubItem !== undefined)
    {
      const indexConvToUpdate = conversationsCopy.findIndex(
        item => item.ConversationId === (replySubItem as any).ConversationId,
      );
      if (indexConvToUpdate >= 0 && conversationsCopy[indexConvToUpdate])
      {
        const replies = [...conversationsCopy[indexConvToUpdate].Replies];
        const replyIndex = replies.findIndex(
          (r: any) => r && r.ReplyId === (replySubItem as any).ReplyId,
        );

        if (replyIndex >= 0)
        {
          replies.splice(replyIndex, 1);
          let totalTasks = conversationsCopy[indexConvToUpdate].TotalTasks - 1;
          if (totalTasks < 0) totalTasks = 0;

          conversationsCopy[indexConvToUpdate] = {
            ...conversationsCopy[indexConvToUpdate],
            Replies: replies,
            TotalTasks: totalTasks,
          };
        }
      }
    }

    this.setState({ conversations: conversationsCopy });
  };

  private onRestoreConversation = (convId: number) =>
  {
    const conversationsCopy = [...this.state.conversations];
    const indexConv = conversationsCopy.findIndex(item => item.ConversationId === convId);

    if (indexConv >= 0)
    {
      conversationsCopy[indexConv] = {
        ...conversationsCopy[indexConv],
        IsDeleted2: false,
      } as any;
    }

    this.setState({ conversations: conversationsCopy });
  };

  private onAddReply = (convId: number, reply: Reply, sendUpdate = true) =>
  {
    const conversationsCopy = [...this.state.conversations];
    const indexConv = conversationsCopy.findIndex(item => item.ConversationId === convId);
    let isSameReply = false;

    if (indexConv >= 0 && conversationsCopy[indexConv])
    {
      const replies = [...conversationsCopy[indexConv].Replies];
      isSameReply =
        replies.findIndex((item: any) => item.ReplyId === (reply as any).ReplyId) !== -1;

      if (!isSameReply)
      {
        replies.push(reply);
      }

      const conv = { ...conversationsCopy[indexConv], Replies: replies };

      if ((reply as any).OriginalConversationId > 0)
      {
        conv.TotalTasks += 1;
      }
      else
      {
        conv.TotalReplies = conv.TotalReplies + (isSameReply ? 0 : 1);
      }

      conversationsCopy[indexConv] = conv;
      this.setState({ conversations: conversationsCopy });

      if (sendUpdate)
      {
        this.props.request.functions?.onAddReply?.(convId, reply, { ...conv });
      }
    }
  };

  private onUpdateExternalReply = (
    convId: number,
    replyId: number,
    newAttrs: Partial<Reply>,
    replace = false,
  ) =>
  {
    const conversationsCopy = [...this.state.conversations];
    const indexConv = conversationsCopy.findIndex(item => item.ConversationId === convId);

    if (indexConv >= 0 && conversationsCopy[indexConv])
    {
      const replies = [...conversationsCopy[indexConv].Replies];
      const indexReply = replies.findIndex((item: any) => item.ReplyId === replyId);

      if (indexReply >= 0 && replies[indexReply])
      {
        if (replace)
        {
          replies[indexReply] = newAttrs as Reply;
        }
        else
        {
          replies[indexReply] = { ...replies[indexReply], ...newAttrs };
        }
        conversationsCopy[indexConv] = { ...conversationsCopy[indexConv], Replies: replies };
        this.setState({ conversations: conversationsCopy });
      }
    }
  };

  private onUpdateReply = (
    convId: number,
    replyId: number,
    newAttrs: Partial<Reply>,
    newTotalFavorites?: number,
    sendUpdate = true,
  ) =>
  {
    const conversationsCopy = [...this.state.conversations];
    const indexConv = conversationsCopy.findIndex(item => item.ConversationId === convId);

    let replyUpdated: Reply | undefined;
    if (indexConv >= 0 && conversationsCopy[indexConv])
    {
      const replies = [...conversationsCopy[indexConv].Replies];
      const indexReply = replies.findIndex((item: any) => item.ReplyId === replyId);

      if (indexReply >= 0 && replies[indexReply])
      {
        replies[indexReply] = { ...replies[indexReply], ...newAttrs };
        replyUpdated = replies[indexReply];
        conversationsCopy[indexConv] = { ...conversationsCopy[indexConv], Replies: replies };
        this.setState({ conversations: conversationsCopy });
      }
    }

    if (sendUpdate && replyUpdated)
    {
      this.props.request.functions?.onUpdateReply?.(
        conversationsCopy[indexConv]!,
        replyUpdated,
        newAttrs as any,
        newTotalFavorites,
      );
    }
  };

  private onDeleteReply = (
    convId: number,
    replyId: number,
    callback?: (totalReplies?: number) => void,
  ) =>
  {
    const conversationsCopy = [...this.state.conversations];
    if (!isNaN(convId))
    {
      const indexConvToUpdate = conversationsCopy.findIndex(item => item.ConversationId === convId);
      let totalReplies: number | undefined;

      if (indexConvToUpdate >= 0 && !isNaN(replyId))
      {
        const replies = [...conversationsCopy[indexConvToUpdate]!.Replies];
        const indexReplyToDelete = replies.findIndex((item: any) => item.ReplyId === replyId);

        if (indexReplyToDelete >= 0)
        {
          // Decrease TotalFavorites if reply was marked as favorite
          let totalFavorites = conversationsCopy[indexConvToUpdate]!.TotalFavorites;
          if ((replies[indexReplyToDelete] as any).IsFavorite)
          {
            totalFavorites--;
          }

          replies.splice(indexReplyToDelete, 1);
          const newTotalReplies = conversationsCopy[indexConvToUpdate]!.TotalReplies - 1;
          totalReplies = newTotalReplies;

          conversationsCopy[indexConvToUpdate] = {
            ...conversationsCopy[indexConvToUpdate]!,
            Replies: replies,
            TotalReplies: newTotalReplies,
            TotalFavorites: totalFavorites,
          } as any;
        }
      }

      this.setState({ conversations: conversationsCopy }, () =>
      {
        if (typeof callback === 'function')
        {
          callback(totalReplies);
        }
      });
    }
  };

  private onAddLayerToCard = (
    e: React.MouseEvent | MouseEvent,
    layer: any,
    functions?: { onUpdateLayerToCard?: (layerId: number) => void },
    conversationId?: number,
  ) =>
  {
    if (this.props.request.functions?.addLayerToCard && conversationId)
    {
      const conversationsCopy = [...this.state.conversations];
      const indexConv = conversationsCopy.findIndex(item => item.ConversationId === conversationId);
      if (indexConv >= 0)
      {
        const conversationCopy = { ...conversationsCopy[indexConv] };
        if ((conversationCopy as any).ConfigValueId === undefined)
        {
          (conversationCopy as any).ConfigValueId = (conversationCopy as any).selectedConfigValueId;
        }

        this.props.request.functions.addLayerToCard(
          e,
          conversationCopy as any,
          layer,
          (layerId: number) =>
          {
            functions?.onUpdateLayerToCard?.(layerId);
          },
        );
      }
    }
  };

  private onCreateNewItem = (item: any, id: number) =>
  {
    const { conversations } = this.state;
    const indexConv = conversations.findIndex(conv => conv.ConversationId === id);

    if (indexConv >= 0 && conversations[indexConv])
    {
      this.props.request.functions?.onCreateItemFromCustomType?.(item, conversations[indexConv]);
    }
  };

  private onConvertTo = (conversation: ConversationDetail) =>
  {
    this.props.request.functions?.onItemConvertTo?.(conversation);
  };

  private onDuplicateItem = (conversation: ConversationDetail) =>
  {
    this.props.request.functions?.onDuplicateItem?.(conversation);
  };

  // ==================== Layout ====================

  private updateDimensions = () =>
  {
    // Force re-render to pick up new window dimensions
    this.forceUpdate();
  };

  private onScroll = () =>
  {
    // Close any open dropdown menus on scroll
    document.querySelectorAll('.note-btn-group.btn-group.open').forEach(el =>
    {
      el.classList.remove('open');
    });
    document.querySelectorAll('.cw-action-menu.dropdown.open').forEach(el =>
    {
      el.classList.remove('open');
    });
  };

  // ==================== Render ====================

  render()
  {
    const {
      show,
      fullScreen,
      conversations,
      isLoading,
      word,
      isViewCard,
      isLoadingFilter,
      hasArchivedFilter,
      loadMoreInProgress,
      hasMoreItems,
      isErrorFetch,
      targetRect,
    } = this.state;
    const { request } = this.props;

    if (!show) return null;

    const SocketConnectionId = this.getSocketConnectionId();

    const cardButtonCss = `corgee_com_button-top-search enable-help-tooltip${isViewCard ? ' current' : ''}`;
    const tileButtonCss = `corgee_com_button-top-search enable-help-tooltip${!isViewCard ? ' current' : ''}`;

    const fullScreenClass = fullScreen ? 'fullscreen_exit' : 'fullscreen';

    // Compute popover style
    const popoverStyle: React.CSSProperties = fullScreen
      ? {
        height: window.innerHeight - 50,
        width: window.innerWidth - (targetRect?.left ?? 200),
        top: 50,
        left: targetRect?.left ?? 200,
      }
      : {
        height: window.innerHeight - (targetRect?.top ?? 80),
        left: targetRect?.left ?? 200,
        top: targetRect?.top ?? 80,
        minWidth: 500,
        maxWidth: 500,
        width: 500,
      };

    return (
      <>
        {/* Backdrop overlay to close on click outside */}
        <div className="top-search-result-overlay" onClick={this.hide} />

        <div ref={this.popoverRef} className="corgee_com_top-search-results" style={popoverStyle}>
          {isLoading && (
            <div className="top-search-loading-spinner">
              <div className="spinner"></div>
            </div>
          )}

          {!isLoading &&
            (conversations && conversations.length > 0 ? (
              <div className="corgee_com_top-search-results-container" tabIndex={-1}>
                <div className="corgee_com_top-search-results-tab-bar">
                  <div className="corgee_com_top-search-results-tab-to-pin">
                    <span className="corgee_com_top-search-results-tab-title">
                      Search results: {word}
                    </span>
                    <button
                      className="corgee_com_button corgee_com_button-top-search enable-help-tooltip"
                      onClick={this.saveSearchResult}
                      data-title="Save to Dashboard"
                    >
                      <i className="fa fa-thumb-tack"></i>
                    </button>
                  </div>
                  <div className="corgee_com_button-container">
                    <button
                      className="corgee_com_button corgee_com_button-top-search"
                      onClick={this.fullScreen}
                    >
                      <i className="material-icons">{fullScreenClass}</i>
                    </button>
                    <button
                      className="corgee_com_button corgee_com_button-top-search"
                      onClick={this.hideAndClear}
                    >
                      <i className="icon-incorrect-icon"></i>
                    </button>
                  </div>
                </div>

                <div className="corgee_com_top-search-results-viewbar">
                  <div className="corgee_com_top-search-results-viewbar-input-filter">
                    <InputFilter
                      request={request}
                      getConversationByFilters={this.getConversationByFilters}
                    />
                  </div>
                  <div className="corgee_com_top-search-results-viewbar-view-type">
                    <button
                      className={cardButtonCss}
                      type="button"
                      onClick={this.showCardView}
                      data-title="Large Cards View"
                    >
                      <i className="icons-corgee-card material-icons"></i>
                    </button>
                    <button
                      className={tileButtonCss}
                      type="button"
                      onClick={this.showTileView}
                      data-title="Small Cards View"
                    >
                      <i className="icons-corgee-title-card material-icons"></i>
                    </button>
                  </div>
                </div>

                <div
                  id="viewport-container-react"
                  className="corgee_com_top-search-results-dash-container"
                >
                  {isLoadingFilter ? (
                    <div className="top-search-loading-spinner">
                      <div className="spinner"></div>
                    </div>
                  ) : isErrorFetch ? (
                    <p style={{ textAlign: 'center', marginTop: 15 }}>
                      Sorry, something went wrong, please try later.
                    </p>
                  ) : (
                    <ol
                      className="corgee_com_top-search-results-dash-list dashboardGroupSelector"
                      onScroll={this.onScroll}
                    >
                      {conversations.map((conversationItem, index) => (
                        <ConversationItem
                          key={'conv-result-' + conversationItem.ConversationId}
                          index={index}
                          api={request.api}
                          corgeeFunctions={{
                            ...(request.functions as any),
                            ...this.props.corgeeFunctions,
                            onNavigateToProject: this.hide,
                          }}
                          conversation={conversationItem}
                          isViewCard={isViewCard}
                          onUpdateConversation={this.onUpdateConversation}
                          onAddReply={this.onAddReply}
                          onDeleteReply={this.onDeleteReply}
                          onCreateNewItem={this.onCreateNewItem as any}
                          onConvertTo={this.onConvertTo}
                          onUpdateReply={this.onUpdateReply}
                          onAddLayerToCard={this.onAddLayerToCard as any}
                          hasArchivedFilter={hasArchivedFilter}
                          realItemTypes={
                            request.params?.RealItemTypes ?? {
                              Tasks: { Name: 'Tasks', Icon: 'icons-menu-epic' },
                              Users: { Name: 'People', Icon: 'icons-menu-account-box' },
                            }
                          }
                          config={request.config}
                          storageData={request.storageData}
                          chatData={request.chatData}
                          onSubmitTime={this.onSubmitTime}
                          SocketConnectionId={SocketConnectionId}
                          userData={request.userData}
                          onDuplicateItem={this.onDuplicateItem}
                          HasItemCreationPermission={request.params?.HasItemCreationPermission}
                        />
                      ))}
                      {hasMoreItems &&
                        conversations.length > 0 &&
                        (loadMoreInProgress ? (
                          <li>
                            <p className="loading-more-items">
                              Loading more items<span>.</span>
                              <span>.</span>
                              <span>.</span>
                            </p>
                          </li>
                        ) : (
                          <li ref={this.sentinelRef} style={{ height: 1 }} />
                        ))}
                    </ol>
                  )}
                </div>
              </div>
            ) : (
              <div className="no-results-found-in-advanced-search-window">
                <span>
                  {isErrorFetch
                    ? 'Sorry, something went wrong, please try later.'
                    : 'No results found.'}
                </span>
                <button
                  className="corgee_com_button corgee_com_button-top-search"
                  onClick={this.hideAndClear}
                >
                  <i className="icon-incorrect-icon"></i>
                </button>
              </div>
            ))}
        </div>
      </>
    );
  }
}

export default TopSearchResult;
